import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/admin/_common/ui/dialog";
import { Input } from "@/app/admin/_common/ui/input";
import { Label } from "@/app/admin/_common/ui/label";
import { Checkbox } from "@/app/admin/_common/ui/checkbox";
import CuoButton from "@/app/admin/_common/CuoButton";
import { useRoleFormFlow } from "./useRoleFormFlow";
import type { Permission, Role } from "@/app/_services/RbacService";
import { ShieldCheck } from "lucide-react";

export const RoleDialog = ({
  open,
  editingRole,
  groupedPermissions,
  onClose,
  onSaved,
}: {
  open: boolean;
  editingRole?: Role | null;
  groupedPermissions: [string, Permission[]][];
  onClose: () => void;
  onSaved: () => void;
}) => {
  const form = useRoleFormFlow({
    editingRole,
    onSaved: () => {
      onSaved();
      onClose();
    },
  });

  const isEditing = Boolean(editingRole);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="inset-0 left-0 top-0 m-auto h-fit max-h-[85vh] w-full max-w-[620px] translate-x-0 translate-y-0 bg-white">
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2 align-middle">
              <ShieldCheck className="size-4 flex-none text-muted-foreground" />
              {isEditing ? editingRole!.name : "New role"}
            </span>
          </DialogTitle>
          <DialogDescription>
            Choose what someone with this role can see and do.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto py-2">
          <div className="grid gap-3">
            <Label htmlFor="role-name">Name</Label>
            <Input
              id="role-name"
              value={form.name}
              onChange={(event) => form.setName(event.target.value)}
              disabled={editingRole?.is_system}
              placeholder="Box office"
            />
            {form.error && (
              <span className="text-[12px] text-red-600">{form.error}</span>
            )}
          </div>

          <div className="grid gap-3">
            <Label htmlFor="role-description">Description</Label>
            <Input
              id="role-description"
              value={form.description}
              onChange={(event) => form.setDescription(event.target.value)}
              placeholder="What this role is for"
            />
          </div>

          <div className="grid gap-3">
            <Label>Permissions</Label>
            <div className="grid gap-4 rounded-md border border-border p-3">
              {groupedPermissions.map(([group, items]) => {
                const ids = items.map((item) => item.id);
                const allChecked = ids.every((id) => form.selected.includes(id));
                return (
                  <div key={group} className="grid gap-2">
                    <label className="flex cursor-pointer items-center gap-2 border-b border-border pb-1.5">
                      <Checkbox
                        checked={allChecked}
                        onCheckedChange={(checked) =>
                          form.toggleGroup(ids, checked === true)
                        }
                      />
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {group}
                      </span>
                    </label>
                    <div className="grid gap-1.5 sm:grid-cols-2">
                      {items.map((permission) => (
                        <label
                          key={permission.id}
                          className="flex cursor-pointer items-center gap-2"
                        >
                          <Checkbox
                            checked={form.selected.includes(permission.id)}
                            onCheckedChange={() =>
                              form.togglePermission(permission.id)
                            }
                          />
                          <span className="text-[12.5px]">{permission.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <CuoButton variant="outline" label="Cancel" onClick={onClose} />
          <CuoButton
            disableOnAsync={form.isSaving}
            variant="default"
            onClick={form.handleSave}
            label="Save"
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
